import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Row } from '@tanstack/react-table'
import DataTable from 'components/DataTable/DataTable/DataTable'
import {
  DataTableProvider,
  useDataTableContext,
} from 'components/DataTable/DataTableContext'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { cn } from 'components/UI/cn'
import apiService from 'services/api'
import { EmailThread } from 'models/emailIntegration'
import { InboxTab, InboxTabs } from './InboxTabs'
import { InboxSearchBar } from './InboxSearchBar'
import { InboxFilterChips, InboxFilters } from './InboxFilterChips'
import { InboxActionBar } from './InboxActionBar'
import { useInboxThreadColumns } from './useInboxThreadColumns'

/** Sync total backend row count into DataTableContext for correct bulk-select counts. */
function useSyncTotalRows(total: number) {
  const {
    methods: { setTotalRowsInBackend },
  } = useDataTableContext()

  useEffect(() => {
    setTotalRowsInBackend(total)
  }, [total, setTotalRowsInBackend])
}

/**
 * Tailwind overrides for compact email row styling.
 * The DataTable uses styled-components with hardcoded 56px rows.
 * These arbitrary selectors override to match the Figma email row spec (38px).
 */
const EMAIL_TABLE_OVERRIDES = cn(
  // Hide table header (email columns have no visible headers)
  '[&_thead]:!hidden',
  // Compact row height (38px per Figma)
  '[&_tr]:!h-[38px]',
  '[&_td]:!h-[38px]',
  // Cell padding: 16px on edges, 12px between cells
  '[&_td]:!pl-3 [&_td:first-child]:!pl-4 [&_td:last-child]:!pr-4',
  // Row divider per Figma (#E2E2E2)
  '[&_td]:!border-b-[#E2E2E2]',
  // Selected row bg (#D6E2DA) and left bar (#8EA69B)
  '[&_tr[data-selected]]:!bg-[#D6E2DA]',
  '[&_tr[data-selected]]:!shadow-[inset_3px_0_0_0_#8EA69B]',
  // Hover shadow per Figma
  '[&_tr:hover]:!shadow-[0px_1px_3px_1px_rgba(60,64,67,0.15),0px_1px_2px_0px_rgba(60,64,67,0.3),-1px_0px_0px_0px_#dadce0,1px_0px_0px_0px_#dadce0]',
  // Selected + hover keeps selected bg
  '[&_tr[data-selected]:hover]:!bg-[#D6E2DA]',
  '[&_tr[data-selected]:hover]:!shadow-[inset_3px_0_0_0_#8EA69B,0px_1px_3px_1px_rgba(60,64,67,0.15),0px_1px_2px_0px_rgba(60,64,67,0.3),-1px_0px_0px_0px_#dadce0,1px_0px_0px_0px_#dadce0]'
)

export const PAGE_SIZE = 50

interface EmailInboxViewProps {
  contactCompanyId: number
  forDoorProxyId?: number
  selectedTab: InboxTab
  onTabChange: (tab: InboxTab) => void
  onThreadClick: (
    thread: EmailThread,
    index: number,
    threads: EmailThread[],
    page: number,
    total: number
  ) => void
  onCompose?: () => void
}

/**
 * Container component for the thread-based inbox list view.
 * Composes search, filters, tabs, action bar, and DataTable.
 */
export function EmailInboxView({
  contactCompanyId,
  forDoorProxyId,
  selectedTab,
  onTabChange,
  onThreadClick,
  onCompose,
}: EmailInboxViewProps) {
  const api = apiService()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<InboxFilters>({})
  const columns = useInboxThreadColumns()

  // Reset page when filters or tab change
  useEffect(() => {
    setPage(1)
  }, [search, filters, selectedTab])

  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      page,
      page_size: PAGE_SIZE,
      my_emails: selectedTab === 'my_emails',
    }

    if (forDoorProxyId) {
      params.door_proxy_id = forDoorProxyId
    } else {
      params.chain_proxy_id = contactCompanyId
    }

    if (search) {
      params.search = search
    }

    if (filters.from_email) params.from_email = filters.from_email
    if (filters.to_email) params.to_email = filters.to_email
    if (filters.date_greater) params.date_greater = filters.date_greater
    if (filters.date_less) params.date_less = filters.date_less
    if (filters.status) params.status = filters.status
    if (filters.has_attachment) params.has_attachment = filters.has_attachment

    return params
  }, [page, selectedTab, contactCompanyId, forDoorProxyId, search, filters])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['email-threads', queryParams],
    queryFn: ({ signal }) =>
      api.emailIntegration.getEmailThreadList(queryParams, signal),
  })

  const threads = data?.results ?? []
  const total = data?.count ?? 0

  const handleRowClick = useCallback(
    (row: Row<EmailThread>) => {
      onThreadClick(row.original, row.index, threads, page, total)
    },
    [onThreadClick, threads, page, total]
  )

  const tableKey = forDoorProxyId
    ? `email-threads-door-${forDoorProxyId}`
    : `email-threads-company-${contactCompanyId}`

  return (
    <div className="h-full flex flex-col gap-3">
      <InboxSearchBar
        value={search}
        onChange={setSearch}
        onCompose={onCompose}
      />
      <InboxFilterChips filters={filters} onChange={setFilters} />
      <ColumnsStoreProvider tableKey="EMAIL_THREADS_TABLE">
        <DataTableProvider tableKey={tableKey}>
          <InboxTableContent
            page={page}
            total={total}
            threads={threads}
            columns={columns}
            tableKey={tableKey}
            isLoading={isLoading}
            selectedTab={selectedTab}
            onPageChange={setPage}
            onTabChange={onTabChange}
            onRefresh={() => refetch()}
            onRowClick={handleRowClick}
          />
        </DataTableProvider>
      </ColumnsStoreProvider>
    </div>
  )
}

/** Inner content rendered inside DataTableProvider so it can access context. */
function InboxTableContent({
  page,
  total,
  threads,
  columns,
  tableKey,
  isLoading,
  selectedTab,
  onPageChange,
  onTabChange,
  onRefresh,
  onRowClick,
}: {
  page: number
  total: number
  threads: EmailThread[]
  columns: ReturnType<typeof useInboxThreadColumns>
  tableKey: string
  isLoading: boolean
  selectedTab: InboxTab
  onPageChange: (page: number) => void
  onTabChange: (tab: InboxTab) => void
  onRefresh: () => void
  onRowClick: (row: Row<EmailThread>) => void
}) {
  useSyncTotalRows(total)

  return (
    <div className="flex-1 min-h-0 flex flex-col rounded-xl shadow-[0px_1px_3px_0px_rgba(16,24,40,0.1),0px_1px_2px_0px_rgba(16,24,40,0.06)] overflow-hidden">
      <InboxActionBar
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        threads={threads}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
      />
      <InboxTabs selectedTab={selectedTab} onTabChange={onTabChange} />
      <div
        className={cn('flex-1 min-h-0 overflow-auto', EMAIL_TABLE_OVERRIDES)}
      >
        <DataTable<EmailThread>
          columns={columns}
          data={threads}
          tableKey={tableKey}
          loading={isLoading}
          virtualizeRows={false}
          isPaginationEnabled={false}
          enableRowSelection={true}
          onRowClick={onRowClick}
          emptyTableText={{
            title: 'No email threads found',
            subtitle: 'Try adjusting your filters or search terms',
          }}
        />
      </div>
    </div>
  )
}
